import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  type DesignQuestion,
  type PlanBoard,
  type PlanOp,
  type PlanSnapshot,
  type PlanTask,
  type RunReport,
  type ScheduleSpec,
  type SkillBundle,
} from '../api'
import { CosmicDiagram } from '../diagrams/CosmicDiagram'
import { Packet } from '../art/Packet'
import { parseDiagram } from '../diagrams/parse'
import type { LiveTask } from '../plan/PlanProvider'

function groupByDomain(skills: SkillBundle[]): Record<string, SkillBundle[]> {
  const out: Record<string, SkillBundle[]> = {}
  for (const s of skills) {
    ;(out[s.domain] ??= []).push(s)
  }
  return out
}


const NODE_W = 200
const NODE_H = 86
const COL_GAP = 70
const ROW_GAP = 22
const PAD = 14

function probColor(p: number): string {
  if (p >= 0.75) return 'var(--ok)'
  if (p >= 0.55) return 'var(--warn)'
  return 'var(--error)'
}

// NOTE: the full-page PlanningView component and PlanSwitcher used to live
// here. The console replaced the former (nothing imported it), and the latter
// moved to PlanSwitcher.tsx so the console can import it without dragging this
// module — and every fullscreen artifact body in it — into the initial bundle.





// Design-doc generation runs ~3-5 minutes across two LLM passes; DesignDocProgress turns the
// wait into honest stages driven by live SSE phase events from the backend.
const DESIGNDOC_PHASES = [
  'Searching the project for insights',
  'Designing diagrams & open questions',
  'Assembling the document',
  'Publishing',
]
function DesignDocProgress({ elapsed, active }: { elapsed: number; active: number }) {
  const mm = Math.floor(elapsed / 60)
  const ss = Math.floor(elapsed % 60)
  const clock = `${mm}:${ss.toString().padStart(2, '0')}`
  return (
    <div className="runpanel is-running decompose-progress">
      <div className="runpanel-head">
        <strong>Claude is authoring the design doc…</strong>
        <span className="runpanel-stat">
          {clock} · usually 3–5 min
        </span>
      </div>
      <ul className="decphase-rows">
        {DESIGNDOC_PHASES.map((label, i) => {
          const state = i < active ? 'done' : i === active ? 'active' : 'pending'
          return (
            <li key={label} className={`decphase decphase-${state}`}>
              <span className="decphase-dot" />
              <span className="decphase-label">{label}</span>
              {state === 'active' ? <span className="decphase-spin" /> : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ErrorPanel surfaces a failed decompose/run inline with a one-click Retry, instead
// of a terse string (or, worse, replacing the whole view). Inputs are preserved so
// Retry re-runs the same action without re-typing.
export function ErrorPanel({
  title,
  message,
  onRetry,
  onDismiss,
}: {
  title: string
  message: string
  onRetry: () => void
  onDismiss: () => void
}) {
  return (
    <div className="errorpanel" role="alert">
      <div className="errorpanel-body">
        <strong className="errorpanel-title">⚠ {title}</strong>
        <p className="errorpanel-msg">{message}</p>
      </div>
      <div className="errorpanel-actions">
        <button className="errorpanel-retry" onClick={onRetry}>
          ↻ Retry
        </button>
        <button className="errorpanel-dismiss" onClick={onDismiss} aria-label="Dismiss">
          ✕
        </button>
      </div>
    </div>
  )
}



// PlanExecution is the finalize gateway (J3/J4). A DRAFT shows a prominent Finalize CTA and no
// run controls — you can't run something you haven't committed. A FINALIZED plan shows how to
// run it: Run once now (the default for a one-off build), with Reopen to return to editing.
export function PlanExecution({
  board,
  busy,
  running,
  unansweredCritical,
  onFinalize,
  onReopen,
  onRunOnce,
  onStop,
  onSchedule,
  onUnschedule,
}: {
  board: PlanBoard
  busy: boolean
  running: boolean
  unansweredCritical: number
  onFinalize: () => void
  onReopen: () => void
  onRunOnce: (mode?: 'step') => void
  onStop: () => void
  onSchedule: (spec: ScheduleSpec) => void
  onUnschedule: () => void
}) {
  if (board.status !== 'finalized') {
    const blocked = unansweredCritical > 0
    return (
      <section className="plan-exec draft">
        <div className="plan-exec-copy">
          <strong>Draft — not yet runnable</strong>
          <span>Revise and review, then finalize to unlock running.</span>
        </div>
        <button
          className="plan-exec-finalize"
          onClick={onFinalize}
          disabled={busy || blocked}
          title={
            blocked
              ? `Answer ${unansweredCritical} critical open question${unansweredCritical === 1 ? '' : 's'} to finalize.`
              : 'Finalize this plan — commits it and unlocks execution.'
          }
        >
          {blocked ? `🔒 Answer ${unansweredCritical} to finalize` : '✓ Finalize plan'}
        </button>
      </section>
    )
  }
  const runBlocked = unansweredCritical > 0
  return (
    <section className={`plan-exec final${runBlocked ? ' gated' : ''}`}>
      <div className="plan-exec-copy">
        <strong>
          {runBlocked
            ? `✋ ${unansweredCritical} critical question${unansweredCritical === 1 ? '' : 's'} must be answered before this plan can run`
            : '✓ Finalized — ready to run'}
        </strong>
        <button
          className="plan-exec-reopen"
          onClick={onReopen}
          disabled={busy || running}
          title="Reopen for editing (returns the plan to draft)"
        >
          ↩ Reopen
        </button>
        <a className="plan-exec-link" href="#/runs" title="Run history and the review station">
          View runs →
        </a>
      </div>
      <div className="plan-exec-actions">
        {running ? (
          <button className="run-stop" onClick={onStop} title="Stop the in-flight run (cancels the running task, marks the run interrupted)">
            ■ Stop
          </button>
        ) : (
          <>
            <button
              className="plan-exec-run"
              onClick={() => onRunOnce()}
              disabled={busy || runBlocked}
              title={
                runBlocked
                  ? 'Answer the critical open questions below — the run gate stays closed until every critical has an answer.'
                  : 'Run the plan once now in an isolated worktree. The work is captured as a reviewable diff — nothing is applied to the target.'
              }
            >
              ▶ Run once
            </button>
            <button
              className="plan-exec-run step"
              onClick={() => onRunOnce('step')}
              disabled={busy || runBlocked}
              title="Manual mode: tasks run one at a time, and each task's diff must be approved before the next launches. Nothing proceeds unvalidated."
            >
              ⏯ Step run
            </button>
            <ScheduleControl schedule={board.schedule} busy={busy} nudge={board.project_type === 'workflow'} onSchedule={onSchedule} onUnschedule={onUnschedule} />
          </>
        )}
      </div>
    </section>
  )
}

// ScheduleControl is the opt-in half of J4: run the plan recurring (every N hours) or once at a
// future time, instead of the default run-once. Shows the active schedule when one is set.
function fmtEvery(minutes: number): string {
  if (minutes % 1440 === 0) return `${minutes / 1440}d`
  if (minutes % 60 === 0) return `${minutes / 60}h`
  return `${minutes}m`
}
function ScheduleControl({
  schedule,
  busy,
  nudge,
  onSchedule,
  onUnschedule,
}: {
  schedule?: ScheduleSpec
  busy: boolean
  nudge?: boolean // workflow-type plans: scheduling is the point, so highlight it
  onSchedule: (spec: ScheduleSpec) => void
  onUnschedule: () => void
}) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<'every' | 'once'>('every')
  const [everyHours, setEveryHours] = useState(24)
  const [at, setAt] = useState('')

  if (schedule) {
    const label = schedule.kind === 'every' ? `every ${fmtEvery(schedule.every_minutes ?? 0)}` : `once at ${schedule.at ? new Date(schedule.at).toLocaleString() : '—'}`
    return (
      <span className="plan-sched active" title={schedule.next_run ? `Next run: ${new Date(schedule.next_run).toLocaleString()}` : undefined}>
        ⏱ Scheduled {label}
        <button className="plan-sched-clear" onClick={onUnschedule} disabled={busy} title="Cancel the schedule (back to run-once)">
          ✕
        </button>
      </span>
    )
  }
  if (!open) {
    return (
      <button
        className={nudge ? 'plan-exec-schedule nudge' : 'plan-exec-schedule'}
        onClick={() => setOpen(true)}
        disabled={busy}
        title={
          nudge
            ? 'This is a workflow project — scheduling it to recur is the usual next step.'
            : 'Run this plan on a schedule instead of once (opt-in — a build is normally a one-off)'
        }
      >
        🗓 Schedule…
      </button>
    )
  }
  const submit = () => {
    if (kind === 'every') onSchedule({ kind: 'every', every_minutes: Math.max(1, Math.round(everyHours * 60)) })
    else if (at) onSchedule({ kind: 'once', at: new Date(at).toISOString() })
    setOpen(false)
  }
  return (
    <span className="plan-sched form">
      <select value={kind} onChange={(e) => setKind(e.target.value as 'every' | 'once')}>
        <option value="every">Every</option>
        <option value="once">Once at</option>
      </select>
      {kind === 'every' ? (
        <>
          <input type="number" min={1} value={everyHours} onChange={(e) => setEveryHours(Number(e.target.value))} />
          <span className="plan-sched-unit">hours</span>
        </>
      ) : (
        <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
      )}
      <button className="plan-sched-set" onClick={submit} disabled={busy || (kind === 'once' && !at)}>
        Set
      </button>
      <button className="plan-sched-cancel" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </span>
  )
}




// ProjectDesignDoc embeds a full design doc right after the plan: a hybrid of structure
// rendered from the plan (intent, the interactive architecture graph, the staged work
// breakdown) + an LLM-authored narrative (rationale, data model, risks) generated on demand.
export function ProjectDesignDoc({
  board,
  onSelect,
  live,
  onGenerate,
  generating,
  error,
  elapsed,
  active,
}: {
  board: PlanBoard
  onSelect: (id: string) => void
  live: Record<string, LiveTask>
  onGenerate: () => void
  generating: boolean
  error: string | null
  elapsed: number
  active: number
}) {
  const [open, setOpen] = useState(true)
  const stages = useMemo(() => {
    const byTier = new Map<number, PlanTask[]>()
    for (const t of board.tasks) {
      const arr = byTier.get(t.tier) ?? []
      arr.push(t)
      byTier.set(t.tier, arr)
    }
    return [...byTier.entries()].sort((a, b) => a[0] - b[0])
  }, [board])

  return (
    <section className="design-doc">
      {board.design_doc && (board.revision ?? 0) > (board.design_doc_rev ?? 0) ? (
        <div className="stale-chip">
          ⚠ plan changed since this doc was written (r{board.design_doc_rev ?? 0} → r{board.revision}) — an update is ready to run
          <button className="stale-update" onClick={onGenerate} disabled={generating} title="Regenerate the doc from the current plan (your approval is the gate — updates never run silently)">
            {generating ? 'Updating…' : '⟳ Update the doc'}
          </button>
        </div>
      ) : null}
      <button className="design-doc-head" onClick={() => setOpen((o) => !o)}>
        <span className="dd-caret">{open ? '▾' : '▸'}</span>
        <span className="dd-title">📄 Project design doc</span>
        <span className="state dd-sub">{board.design_doc ? 'narrative generated' : 'structure from the plan · narrative on demand'}</span>
      </button>
      {open ? (
        <div className="design-doc-body">
          <h3 className="dd-h">Overview</h3>
          <p className="dd-intent">{board.description || 'No description was provided for this plan.'}</p>

          <h3 className="dd-h">Architecture</h3>
          <p className="state dd-note">The plan as a dependency graph — hover a node to trace its flow.</p>
          <PlanGraph board={board} selected={null} onSelect={onSelect} live={live} />

          <h3 className="dd-h">Work breakdown</h3>
          <ol className="dd-stages">
            {stages.map(([tier, tasks]) => (
              <li key={tier}>
                <span className="dd-stage-label">Stage {tier + 1}</span>
                <ul>
                  {tasks.map((t) => (
                    <li key={t.id}>
                      <span className={`chip ${t.assignee}`}>{t.assignee === 'ai' ? 'AI' : 'Specialist'}</span> {t.title}
                      <span className="dd-stage-who"> · {t.assignee === 'ai' ? t.skill?.replace('-bundle', '') : t.owner ? `${t.owner} · ${t.team}` : `${t.team || t.inbox || 'manual-work'} · unclaimed`}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>

          <div className="dd-narrative-head">
            <h3 className="dd-h">Narrative</h3>
            <button
              className="dd-gen"
              onClick={onGenerate}
              disabled={generating}
              title="Narrative only — refresh the prose/diagrams without restructuring the tasks. For the full pass (research → restructure → narrative), use 🧠 Deepen in the header."
            >
              {generating ? 'Generating…' : board.design_doc ? '↻ Refresh narrative' : '✍ Narrative only'}
            </button>
          </div>
          {error ? <p className="state error">{error}</p> : null}
          {generating ? (
            <DesignDocProgress elapsed={elapsed} active={active} />
          ) : board.design_doc ? (
            <Markdown md={board.design_doc} />
          ) : (
            <p className="state dd-empty">
              Generate an LLM-authored design doc — rationale, key decisions, data model, risks,
              plus architecture diagrams and the open questions to resolve before running.
            </p>
          )}
        </div>
      ) : null}
    </section>
  )
}

// DesignQuestions surfaces the design-doc's open questions at the top of the plan, with the
// critical ones highlighted — answering every critical one is what unblocks a run.
export function DesignQuestions({ questions, onAnswer }: { questions: DesignQuestion[]; onAnswer: (id: string, answer: string) => void }) {
  if (questions.length === 0) return null
  const criticalOpen = questions.filter((q) => q.critical && !q.answer?.trim()).length
  // Priority order: unanswered criticals first (they gate running/proposing),
  // then unanswered optionals, answered ones sink to the bottom — stable
  // within each group so questions don't shuffle while you work.
  const rank = (q: DesignQuestion) => (q.answer?.trim() ? 2 : q.critical ? 0 : 1)
  const ordered = [...questions].sort((a, b) => rank(a) - rank(b))
  return (
    <section className={`design-questions${criticalOpen ? ' has-critical' : ''}`}>
      <div className="dq-head">
        <span className="dq-icon">{criticalOpen ? '⚠' : '✓'}</span>
        <span className="dq-title">Open questions</span>
        <span className="dq-sub">
          {criticalOpen
            ? `${criticalOpen} critical question${criticalOpen === 1 ? '' : 's'} to answer before running`
            : 'all critical questions answered — nothing blocking a run'}
        </span>
      </div>
      <ul className="dq-list">
        {ordered.map((q) => (
          <QuestionItem key={q.id} q={q} onAnswer={onAnswer} />
        ))}
      </ul>
    </section>
  )
}

function QuestionItem({ q, onAnswer }: { q: DesignQuestion; onAnswer: (id: string, answer: string) => void }) {
  const [val, setVal] = useState(q.answer ?? '')
  const [editing, setEditing] = useState(!q.answer?.trim())
  const answered = !!q.answer?.trim()
  const save = () => {
    const v = val.trim()
    if (v !== (q.answer ?? '').trim()) onAnswer(q.id, v)
    if (v) setEditing(false)
  }
  return (
    <li className={`dq-item${q.critical ? ' critical' : ''}${answered ? ' answered' : ''}`}>
      <div className="dq-q">
        {/* Only criticals carry a badge. "Optional" sat on the majority of
            tiles, and a label the majority carries is not information — the
            absence of "critical" already says it. */}
        {q.critical ? <span className="dq-badge">critical</span> : null}
        <span className="dq-text">{q.text}</span>
      </div>
      {q.why ? <div className="dq-why">{q.why}</div> : null}
      {answered && !editing ? (
        // A button, not a div: this was the only route to correcting a saved
        // answer and it was mouse-only, unfocusable, and invisible to a screen
        // reader. The check leads the answer — beside the thing it asserts —
        // rather than trailing the question, where it was the hardest token on
        // the card to find and fell outside the panel box at 390px.
        <button type="button" className="dq-answer" onClick={() => setEditing(true)} title="Click to edit">
          <span className="dq-check" aria-hidden="true">
            ✓
          </span>
          <span className="sr-only">Answered. </span>
          <span>{q.answer}</span>
        </button>
      ) : (
        <div className="dq-edit">
          <textarea
            className="dq-input"
            value={val}
            // Fourteen identical unlabelled fields were indistinguishable to a
            // screen reader once the placeholder cleared on first keystroke.
            aria-label={`Answer: ${q.text}`}
            placeholder="Your answer / decision…"
            rows={2}
            onChange={(e) => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                save()
              }
            }}
          />
          <button className="dq-save" onClick={save} disabled={!val.trim()} title="Answers also save on Enter or when the field loses focus">
            Save
          </button>
        </div>
      )}
      {/* The save semantics, on the tile being answered instead of in every
          placeholder. CSS shows it on :focus-within only. */}
      {!answered || editing ? <p className="dq-hint">Saves when you click away — Enter also saves.</p> : null}
    </li>
  )
}

// mdInline renders **bold**, *italic*, and `code` spans within a line.
function mdInline(s: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index))
    if (m[1]) out.push(<strong key={i++}>{m[1]}</strong>)
    else if (m[2]) out.push(<em key={i++}>{m[2]}</em>)
    else if (m[3]) out.push(<code key={i++}>{m[3]}</code>)
    last = re.lastIndex
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}

// Markdown is a lightweight renderer for the generated narrative: ## headings, - bullets,
// paragraphs with inline emphasis, and fenced code blocks. A ```mermaid block is parsed and
// drawn by the cosmic diagram engine (animated-dots flow/sequence/class) — falling back to a
// styled code block if it's outside the supported subset.
function Markdown({ md }: { md: string }) {
  const blocks: ReactNode[] = []
  let para: string[] = []
  let list: string[] = []
  const flushPara = () => {
    if (para.length) { blocks.push(<p key={'p' + blocks.length}>{mdInline(para.join(' '))}</p>); para = [] }
  }
  const flushList = () => {
    if (list.length) {
      const items = list
      blocks.push(<ul key={'u' + blocks.length}>{items.map((li, i) => <li key={i}>{mdInline(li)}</li>)}</ul>)
      list = []
    }
  }
  const lines = md.split(/\r?\n/)
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]
    const fence = raw.trimStart().match(/^```\s*([\w-]*)/)
    if (fence) {
      flushPara(); flushList()
      const lang = fence[1].toLowerCase()
      const code: string[] = []
      i++
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) { code.push(lines[i]); i++ }
      const body = code.join('\n')
      const diagram = lang === 'mermaid' || lang === 'diagram' ? parseDiagram(body) : null
      blocks.push(
        diagram
          ? <CosmicDiagram key={'d' + blocks.length} diagram={diagram} />
          : <pre key={'c' + blocks.length} className="md-code"><code>{body}</code></pre>,
      )
      continue
    }
    const ln = raw.trimEnd()
    const h = ln.match(/^(#{1,4})\s+(.*)$/)
    const li = ln.match(/^\s*[-*]\s+(.*)$/)
    if (h) {
      flushPara(); flushList()
      const lvl = Math.min(h[1].length + 1, 5)
      const tag = `h${lvl}` as 'h2' | 'h3' | 'h4' | 'h5'
      const H = tag
      blocks.push(<H key={'h' + blocks.length}>{mdInline(h[2])}</H>)
    } else if (li) {
      flushPara(); list.push(li[1])
    } else if (ln === '') {
      flushPara(); flushList()
    } else {
      flushList(); para.push(ln)
    }
  }
  flushPara(); flushList()
  return <div className="md">{blocks}</div>
}

interface NodePos {
  x: number
  y: number
}

export function PlanGraph({
  board,
  selected,
  onSelect,
  live = {},
}: {
  board: PlanBoard
  selected: string | null
  onSelect: (id: string) => void
  live?: Record<string, LiveTask>
}) {
  const { pos, width, height } = useMemo(() => {
    const byTier = new Map<number, PlanTask[]>()
    let maxTier = 0
    for (const t of board.tasks) {
      const arr = byTier.get(t.tier) ?? []
      arr.push(t)
      byTier.set(t.tier, arr)
      if (t.tier > maxTier) maxTier = t.tier
    }
    const p = new Map<string, NodePos>()
    let maxRows = 1
    for (let tier = 0; tier <= maxTier; tier++) {
      const arr = (byTier.get(tier) ?? []).slice().sort((a, b) => a.title.localeCompare(b.title))
      arr.forEach((t, i) =>
        p.set(t.id, { x: PAD + tier * (NODE_W + COL_GAP), y: PAD + i * (NODE_H + ROW_GAP) }),
      )
      if (arr.length > maxRows) maxRows = arr.length
    }
    return {
      pos: p,
      width: PAD * 2 + (maxTier + 1) * NODE_W + maxTier * COL_GAP,
      height: PAD * 2 + maxRows * NODE_H + (maxRows - 1) * ROW_GAP,
    }
  }, [board])

  // Hover-focus: hovering a task traces its dependency neighbourhood (its upstream +
  // downstream tasks and the connecting edges) and dims the rest. Ported from the
  // diagram bake-off winner — the same interaction the design doc uses.
  const [hoverId, setHoverId] = useState<string | null>(null)
  const { adj, taskById } = useMemo(() => {
    const m = new Map<string, Set<string>>()
    const by = new Map<string, PlanTask>()
    const ensure = (id: string) => {
      let s = m.get(id)
      if (!s) { s = new Set([id]); m.set(id, s) }
      return s
    }
    for (const t of board.tasks) {
      by.set(t.id, t)
      ensure(t.id)
      for (const dep of t.depends_on ?? []) { ensure(t.id).add(dep); ensure(dep).add(t.id) }
    }
    return { adj: m, taskById: by }
  }, [board])
  const focusSet = hoverId ? adj.get(hoverId) ?? null : null

  const edges: { d: string; key: string; from: string; to: string; ai: boolean }[] = []
  for (const t of board.tasks) {
    const to = pos.get(t.id)
    if (!to) continue
    for (const dep of t.depends_on ?? []) {
      const from = pos.get(dep)
      if (!from) continue
      const x1 = from.x + NODE_W
      const y1 = from.y + NODE_H / 2
      const x2 = to.x
      const y2 = to.y + NODE_H / 2
      edges.push({
        key: dep + '->' + t.id, from: dep, to: t.id,
        ai: taskById.get(dep)?.assignee === 'ai',
        d: `M ${x1} ${y1} C ${x1 + 34} ${y1}, ${x2 - 34} ${y2}, ${x2} ${y2}`,
      })
    }
  }

  // Auto-fit the whole DAG to the available width so the graph isn't trapped in a
  // narrow horizontally-scrolling pane. The user can override with the zoom controls.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = useState(0)
  const [zoom, setZoom] = useState<number | 'fit'>('fit')

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0
      if (w > 0) setContainerW(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const fitScale = containerW > 0 ? Math.min(1, containerW / width) : 1
  const scale = zoom === 'fit' ? fitScale : zoom
  const clampZoom = (v: number) => Math.max(0.3, Math.min(2, v))
  const stepZoom = (delta: number) =>
    setZoom((z) => clampZoom((z === 'fit' ? fitScale : z) + delta))

  return (
    <div className="plan-graph-wrap">
      <div className="plan-graph-controls">
        <button className={zoom === 'fit' ? 'on' : ''} onClick={() => setZoom('fit')} title="Fit the whole graph to view">
          Fit
        </button>
        <button onClick={() => stepZoom(-0.15)} title="Zoom out" aria-label="Zoom out">
          −
        </button>
        <span className="zlabel">{Math.round(scale * 100)}%</span>
        <button onClick={() => stepZoom(0.15)} title="Zoom in" aria-label="Zoom in">
          +
        </button>
        <button className={zoom === 1 ? 'on' : ''} onClick={() => setZoom(1)} title="Actual size (100%)">
          1:1
        </button>
      </div>
      <div className="plan-graph-scroll" ref={scrollRef}>
        <div className="plan-graph-sizer" style={{ width: width * scale, height: height * scale }}>
          <div
            className="plan-graph"
            style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          >
            <svg className="plan-edges" width={width} height={height}>
              {edges.map((e) => {
                const on = !focusSet || e.from === hoverId || e.to === hoverId
                return <path key={e.key} d={e.d} className={`edge${on ? '' : ' faded'}`} />
              })}
              {edges.map((e, i) => {
                const on = !focusSet || e.from === hoverId || e.to === hoverId
                return (
                  <Packet
                    key={'pk-' + e.key}
                    d={e.d}
                    r={3}
                    dur={2 + (i % 4) * 0.45}
                    className={`packet ${e.ai ? 'ai' : 'human'}${on ? '' : ' faded'}`}
                  />
                )
              })}
            </svg>
            {board.tasks.map((t) => {
              const p = pos.get(t.id)
              if (!p) return null
              const lv = live[t.id]?.status
              const on = !focusSet || focusSet.has(t.id)
              return (
                <button
                  key={t.id}
                  className={`tnode ${t.assignee} ${selected === t.id ? 'sel' : ''}${lv ? ` live-${lv}` : ''}${on ? '' : ' faded'}`}
                  style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H }}
                  onClick={() => onSelect(t.id)}
                  onMouseEnter={() => setHoverId(t.id)}
                  onMouseLeave={() => setHoverId(null)}
                >
                  <div className="tnode-top">
                    <span className={`chip ${t.assignee}`}>{t.assignee === 'ai' ? 'AI' : 'Specialist'}</span>
                    {t.pinned ? <span className="pin" title="pinned">📌</span> : null}
                  </div>
                  <div className="tnode-title">{t.title}</div>
                  <div className="tnode-meta">
                    {t.assignee === 'ai' ? t.skill?.replace('-bundle', '') : t.owner ? `${t.owner} · ${t.team}` : `${t.team || t.inbox || 'manual-work'} · unclaimed`}
                  </div>
                  <div className="prob">
                    <div
                      className="prob-fill"
                      style={{ width: `${Math.round(t.success_probability * 100)}%`, background: probColor(t.success_probability) }}
                    />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// RunReportPanel turns the one-line run summary into an in-context monitor: each
// task's submitted outcome with a pass/fail roll-up, so "submit" and "watch what
// happened" live together instead of in a separate Orchestration tab.
export function RunReportPanel({
  report,
  running,
  live = [],
  summary,
  onSelect,
  onOpenRun,
  onStep,
}: {
  report: RunReport | null
  running: boolean
  live?: { task_id: string; title: string; skill?: string; status: string; step_state?: string }[]
  summary?: string
  onSelect: (id: string) => void
  onOpenRun?: (runId: string) => void
  onStep?: (taskId: string, choice: 'approve' | 'reject') => void
}) {
  const results = report?.results ?? []
  const passed = results.filter((r) => r.status === 'passed').length
  const failed = results.filter((r) => r.status === 'failed').length
  const pct = results.length > 0 ? Math.round((passed / results.length) * 100) : 0
  const liveDone = live.filter((t) => t.status !== 'running').length

  return (
    <div className={`runpanel ${running ? 'is-running' : ''}`}>
      <div className="runpanel-head">
        <strong>{running ? 'Running plan…' : 'Run report'}</strong>
        {report?.run_id ? <span className="runpanel-id">run {report.run_id}</span> : null}
        {!running && results.length > 0 ? (
          <span className="runpanel-stat">
            {passed} passed · {failed} failed · {pct}%
          </span>
        ) : running && live.length > 0 ? (
          <span className="runpanel-stat">
            {liveDone}/{live.length} done
          </span>
        ) : null}
        {!running && report?.run_id && onOpenRun ? (
          <button className="runpanel-link" onClick={() => onOpenRun(report.run_id)}>
            View run details →
          </button>
        ) : null}
      </div>
      {!running && results.length > 0 ? (
        <div className="runpanel-bar">
          <div className="runpanel-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {running ? (
        live.length > 0 ? (
          <ul className="runpanel-rows">
            {live.map((t) => (
              <li
                key={t.task_id}
                className={`runpanel-row status-${t.status}${t.step_state === 'awaiting' ? ' step-awaiting' : ''}`}
                onClick={() => onSelect(t.task_id)}
                title={t.step_state === 'awaiting' ? 'Step gate: this task holds its diff — approve to continue or reject to discard' : 'Show this task in the inspector'}
              >
                <span className={`runpanel-dot status-${t.status}`} />
                <span className="runpanel-title">{t.title}</span>
                {t.skill ? <span className="runpanel-skill">{t.skill.replace('-bundle', '')}</span> : null}
                {t.step_state === 'awaiting' && onStep ? (
                  <span className="runpanel-stepgate" onClick={(e) => e.stopPropagation()}>
                    <button className="stepgate approve" onClick={() => onStep(t.task_id, 'approve')} title="Approve this task's work — the next task launches">
                      ✓ Approve
                    </button>
                    <button className="stepgate reject" onClick={() => onStep(t.task_id, 'reject')} title="Reject this task's work — it fails and dependents block">
                      ✗ Reject
                    </button>
                  </span>
                ) : (
                  <span className={`runpanel-badge status-${t.status}`}>
                    {t.step_state === 'awaiting' ? '⏸ step review' : t.status === 'running' ? '● running' : t.status}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="state runpanel-wait">Submitting tasks to the skill runner…</p>
        )
      ) : results.length > 0 ? (
        <ul className="runpanel-rows">
          {results.map((r) => (
            <li
              key={r.task_id}
              className={`runpanel-row status-${r.status}`}
              onClick={() => onSelect(r.task_id)}
              title={r.output ? 'Open the task to see what the agent produced' : 'Show this task in the inspector'}
            >
              <span className={`runpanel-dot status-${r.status}`} />
              <span className="runpanel-rowtext">
                <span className="runpanel-title">{r.title}</span>
                {r.note ? <span className="runpanel-note">{r.note}</span> : null}
              </span>
              {r.skill ? <span className="runpanel-skill">{r.skill.replace('-bundle', '')}</span> : null}
              <span className={`runpanel-badge status-${r.status}`}>{r.status}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="state">{summary ?? 'No tasks ran.'}</p>
      )}
      {!running && summary ? <p className="state runpanel-summary">{summary}</p> : null}
    </div>
  )
}


export function Legend() {
  return (
    <div className="legend">
      <span className="lg">
        <i className="sw ai" /> AI
      </span>
      <span className="lg">
        <i className="sw human" /> Specialist
      </span>
      <span className="lg sep">success:</span>
      <span className="lg">
        <i className="sw g" /> high
      </span>
      <span className="lg">
        <i className="sw y" /> medium
      </span>
      <span className="lg">
        <i className="sw r" /> low
      </span>
    </div>
  )
}

// TargetRepoControl picks the git repo a run executes against. Empty = a greenfield
// sandbox (fresh temp repo) — the safe default that touches nothing. Either way runs
// happen in an isolated worktree and the work is captured as a reviewable diff.
export function TargetRepoControl({ value, onSet }: { value: string; onSet: (repo: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  if (editing) {
    const save = () => {
      onSet(draft.trim())
      setEditing(false)
    }
    return (
      <span className="target-repo editing">
        <input
          className="target-repo-input"
          value={draft}
          autoFocus
          placeholder="path to a git repo (blank = greenfield sandbox)"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') setEditing(false)
          }}
        />
        <button className="target-repo-save" onClick={save}>Set</button>
      </span>
    )
  }
  const label = value || 'greenfield sandbox'
  return (
    <button
      className={`target-repo ${value ? 'set' : 'greenfield'}`}
      onClick={() => { setDraft(value); setEditing(true) }}
      title="The repo a run executes against, in an isolated worktree. Blank = a throwaway greenfield sandbox. Click to change."
    >
      <span className="target-repo-key">target</span>
      <span className="target-repo-val">{label}</span>
    </button>
  )
}

interface GanttEdge {
  key: string
  d: string
  from: string
  to: string
  shade: number // 0..2 — alternating grey, keyed to the target row so lines into the same task share a colour
}

export function GanttView({
  board,
  selected,
  onSelect,
  live = {},
}: {
  board: PlanBoard
  selected: string | null
  onSelect: (id: string) => void
  live?: Record<string, LiveTask>
}) {
  const maxTier = board.tasks.reduce((m, t) => Math.max(m, t.tier), 0)
  const tasks = [...board.tasks].sort((a, b) => a.tier - b.tier || a.title.localeCompare(b.title))
  const cols = `repeat(${maxTier + 1}, minmax(0, 1fr))`

  const [showDeps, setShowDeps] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const barRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const setBarRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) barRefs.current.set(id, el)
    else barRefs.current.delete(id)
  }
  const [edges, setEdges] = useState<GanttEdge[]>([])

  // Bars are placed by CSS grid, so their pixel coordinates only exist after
  // layout. Measure each bar's rect relative to the gantt container and draw a
  // finish→start curve from each predecessor's right edge to its dependent's left.
  useLayoutEffect(() => {
    // Fan-out map: source id → ordered list of the tasks that depend on it. Used to
    // spread the curves' endpoints along each bar's edge so lines sharing a source or
    // target don't stack on the exact same point — making each one's target legible.
    const outMap = new Map<string, string[]>()
    for (const t of board.tasks) {
      for (const dep of t.depends_on ?? []) {
        const arr = outMap.get(dep) ?? []
        arr.push(t.id)
        outMap.set(dep, arr)
      }
    }
    // Row index of each task in display order — used to pick one of three alternating
    // greys per edge (keyed to the target), so neighbouring lines differ and every
    // line into a given task shares a shade.
    const rowOf = new Map<string, number>()
    tasks.forEach((t, i) => rowOf.set(t.id, i))
    const measure = () => {
      const cont = containerRef.current
      if (!cont) return
      const cr = cont.getBoundingClientRect()
      const next: GanttEdge[] = []
      for (const t of board.tasks) {
        const toEl = barRefs.current.get(t.id)
        if (!toEl) continue
        const tr = toEl.getBoundingClientRect()
        const incoming = t.depends_on
        for (let ii = 0; ii < incoming.length; ii++) {
          const dep = incoming[ii]
          const fromEl = barRefs.current.get(dep)
          if (!fromEl) continue
          const fr = fromEl.getBoundingClientRect()
          const outs = outMap.get(dep) ?? [t.id]
          const oi = Math.max(0, outs.indexOf(t.id))
          // Spread endpoints across the bar height (avoid the very edges with 0.18..0.82).
          const spread = (idx: number, n: number) => 0.18 + (0.64 * (idx + 1)) / (n + 1)
          const x1 = fr.right - cr.left
          const y1 = fr.top + fr.height * spread(oi, outs.length) - cr.top
          const x2 = tr.left - cr.left
          const y2 = tr.top + tr.height * spread(ii, incoming.length) - cr.top
          // Curvier than a gentle ease: push control points well past the midpoint
          // (horizontally) and bow them vertically by ~⅓ of the rise, so each line
          // arcs distinctly and only grazes its neighbours.
          const gap = Math.abs(x2 - x1)
          const dx = Math.max(48, Math.min(180, gap * 0.7))
          const bow = (y2 - y1) * 0.32
          next.push({
            key: dep + '->' + t.id,
            from: dep,
            to: t.id,
            shade: (rowOf.get(t.id) ?? 0) % 3,
            d: `M ${x1} ${y1} C ${x1 + dx} ${y1 + bow}, ${x2 - dx} ${y2 - bow}, ${x2} ${y2}`,
          })
        }
      }
      setEdges(next)
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [board])

  return (
    <div className="gantt" ref={containerRef}>
      <button
        className={`gantt-dep-toggle ${showDeps ? 'on' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          setShowDeps((v) => !v)
        }}
        title="Toggle dependency lines"
      >
        ⤳ deps
      </button>
      <div className="gantt-axis">
        <span className="gantt-axis-label">Tasks</span>
        <div className="gantt-ticks" style={{ gridTemplateColumns: cols }}>
          {Array.from({ length: maxTier + 1 }, (_, i) => (
            <span key={i} className="tick">
              stage {i + 1}
            </span>
          ))}
        </div>
      </div>
      {tasks.map((t) => {
        const pct = Math.round(t.success_probability * 100)
        const lv = live[t.id]?.status
        return (
          <div
            key={t.id}
            className={`gantt-row ${selected === t.id ? 'sel' : ''}`}
            onClick={() => onSelect(t.id)}
          >
            <div className="gantt-label" title={t.title + (t.description ? ' — ' + t.description : '')}>
              <span className={`chip ${t.assignee}`}>{t.assignee === 'ai' ? 'AI' : 'Specialist'}</span>
              <div className="gantt-text">
                <div className="gantt-title">
                  {t.title}
                  <button
                    className="task-info"
                    title="Full details of this planned work"
                    onClick={(e) => {
                      e.stopPropagation()
                      onSelect(t.id)
                    }}
                  >
                    ⓘ
                  </button>
                </div>
                <div className="gantt-sub">
                  {t.assignee === 'ai' ? t.skill?.replace('-bundle', '') : t.owner ? `${t.owner} · ${t.team}` : `${t.team || t.inbox || 'manual-work'} · unclaimed`}
                  {t.pinned ? ' · 📌' : ''}
                </div>
              </div>
            </div>
            <div className="gantt-track" style={{ gridTemplateColumns: cols }}>
              <div
                ref={setBarRef(t.id)}
                className={`gantt-bar ${t.assignee}${lv ? ` live-${lv}` : ''}`}
                style={{ gridColumn: t.tier + 1 }}
                title={lv ? `run: ${lv}` : `${pct}% predicted success`}
              >
                <div
                  className="gantt-bar-fill"
                  style={{ width: `${pct}%`, background: probColor(t.success_probability) }}
                />
                <span className="gantt-bar-pct">{pct}%</span>
              </div>
            </div>
          </div>
        )
      })}
      {showDeps && edges.length > 0 ? (
        <svg className="gantt-edges" width="100%" height="100%">
          <defs>
            {[0, 1, 2].map((sh) => (
              <marker
                key={sh}
                id={`gantt-arrow-${sh}`}
                markerWidth="7"
                markerHeight="7"
                refX="6"
                refY="3.5"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M0,0 L7,3.5 L0,7 Z" className={`gantt-arrow-head shade-${sh}`} />
              </marker>
            ))}
          </defs>
          {edges.map((e) => {
            const hot = selected != null && (e.from === selected || e.to === selected)
            return (
              <path
                key={e.key}
                d={e.d}
                className={`gantt-edge shade-${e.shade} ${hot ? 'hot' : ''}`}
                markerEnd={`url(#gantt-arrow-${e.shade})`}
              />
            )
          })}
          {/* flowing packets along each dependency curve — ported from the bake-off winner */}
          {edges.map((e, i) => {
            const hot = selected != null && (e.from === selected || e.to === selected)
            return (
              <Packet
                key={'gp-' + e.key}
                d={e.d}
                r={2.6}
                dur={2.2 + (i % 4) * 0.4}
                className={`gantt-packet shade-${e.shade} ${hot ? 'hot' : ''}`}
              />
            )
          })}
        </svg>
      ) : null}
    </div>
  )
}

export function TaskInspector({
  task,
  snap,
  onClose,
  onOp,
}: {
  task: PlanTask
  snap: PlanSnapshot
  onClose: () => void
  onOp: (op: PlanOp) => void
}) {
  return (
    <div className="inspector">
      <div className="inspector-head">
        <strong>{task.title}</strong>
        <button className="x" onClick={onClose}>
          ✕
        </button>
      </div>
      {task.description ? <p className="inspector-desc">{task.description}</p> : null}
      <div className="work-details">
        <span className="lbl">Planned work</span>
        <ul>
          {task.estimate_hours ? <li><strong>{task.estimate_hours}h</strong> estimated{task.assignee === 'human' ? ' (crew-billed)' : ''}</li> : null}
          {task.inbox && task.assignee === 'human' ? <li>trade: <strong>{task.inbox}</strong></li> : null}
          {(task.depends_on ?? []).length > 0 ? (
            <li>
              waits on:{' '}
              {(task.depends_on ?? [])
                .map((id) => snap.board?.tasks.find((x) => x.id === id)?.title ?? id)
                .join('; ')}
            </li>
          ) : (
            <li>no prerequisites — can start immediately</li>
          )}
          {(task.materials ?? []).length > 0 ? (
            <li>
              materials: {(task.materials ?? []).map((m) => `${m.name} ×${m.qty}${m.unit ?? ''}`).join(', ')}
            </li>
          ) : null}
          <li>status: {task.status}</li>
        </ul>
      </div>
      {task.output || task.run_note ? (
        <details className="task-output" open>
          <summary>
            Agent output{task.run_note ? <span className="task-output-note"> · {task.run_note}</span> : null}
          </summary>
          {task.output ? <pre className="task-output-body">{task.output}</pre> : null}
        </details>
      ) : null}
      <div className="inspector-grid">
        <span className="lbl">Assignee</span>
        <div className="seg">
          <button
            className={task.assignee === 'ai' ? 'on' : ''}
            onClick={() => onOp({ kind: 'assign', task_id: task.id, value: 'ai' })}
          >
            AI
          </button>
          <button
            className={task.assignee === 'human' ? 'on' : ''}
            onClick={() => onOp({ kind: 'assign', task_id: task.id, value: 'human' })}
          >
            Specialist
          </button>
        </div>

        {task.assignee === 'ai' ? (
          <label className="cache-toggle" title="Replay this task's last passed result when its inputs are unchanged (always-fresh is the default for agent tasks)">
            <input
              type="checkbox"
              checked={!!task.cache_ok}
              onChange={(e) => onOp({ kind: 'cache', task_id: task.id, value: e.target.checked ? 'on' : 'off' })}
            />{' '}
            Cache unchanged results
          </label>
        ) : null}
        {task.assignee === 'ai' ? (
          <>
            <span className="lbl">Skill</span>
            <select
              value={task.skill ?? ''}
              onChange={(e) => onOp({ kind: 'skill', task_id: task.id, value: e.target.value })}
            >
              {Object.entries(groupByDomain(snap.skills)).map(([domain, list]) => (
                <optgroup key={domain} label={domain}>
                  {list.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </>
        ) : (
          <>
            <span className="lbl">Routing</span>
            <span className="inbox-route">
              <a href="#/queues" title="Manual work sits unclaimed in its trade queue until a real person claims it — nobody fictional is ever assigned.">
                {task.inbox || 'manual-work'}
              </a>{' '}
              queue ·{' '}
              {task.owner ? (
                <>claimed by <strong>{task.owner}</strong></>
              ) : (
                'unclaimed'
              )}
            </span>
          </>
        )}

        {task.required_skill ? (
          <>
            <span className="lbl">Skill</span>
            <span className="req-skill" title="the unified-taxonomy skill this task needs — who can do it is every discipline that covers it">
              {task.required_skill}
            </span>
          </>
        ) : null}

        {task.assignee === 'ai' ? (
          <>
            <span className="lbl">Success</span>
            <span>
              <strong style={{ color: probColor(task.success_probability) }}>
                {Math.round(task.success_probability * 100)}%
              </strong>{' '}
              predicted
            </span>

            <span className="lbl">Outcome</span>
            <div className="seg">
              <button onClick={() => onOp({ kind: 'outcome', task_id: task.id, success: true })}>👍 succeeded</button>
              <button onClick={() => onOp({ kind: 'outcome', task_id: task.id, success: false })}>🐞 redo/bug</button>
            </div>
          </>
        ) : null}

        <span className="lbl"></span>
        <button className="danger" onClick={() => onOp({ kind: 'remove', task_id: task.id })}>
          Remove task
        </button>
      </div>
    </div>
  )
}
